// Main-thread client for the transform worker. Spawns a FRESH worker per run
// (a transform is a rare, heavy, user-initiated action — no pool to keep warm,
// and a fresh worker guarantees no leaked VM state from a prior run), and always
// terminates it afterwards.
//
// Serialisation choice: we deepToRaw + JSON.stringify the tree HERE, on the main
// thread, and hand the worker a single JSON STRING rather than the object graph.
// Two reasons: (1) the tree is a Vue reactive proxy, which postMessage cannot
// structured-clone at all (DataCloneError) — deepToRaw is mandatory regardless;
// (2) structured-cloning a 57k-node object across the worker boundary is far
// slower and doubles peak memory versus cloning one flat string, and the sandbox
// wants a JSON string anyway (it JSON.parses inside the VM). So stringify-once,
// clone-a-string, parse-once-in-VM beats clone-the-graph-then-stringify-in-worker.

import { deepToRaw } from './deepToRaw';

// Reject a result string larger than this before we ever JSON.parse it — a
// pathological transform could balloon the tree, and parsing 256 MB+ of JSON on
// the main thread would jank or OOM the tab. The caller gets a structured error.
// Callers can pass a (smaller) cap via options.maxResultBytes — inspect-style
// read-only runs use a few KB, since their result goes back to the LLM verbatim.
const MAX_RESULT_BYTES = 256 * 1024 * 1024;

function formatBytes(bytes) {
  if (bytes >= 1024 * 1024) return `${Math.round(bytes / (1024 * 1024))} MB`;
  return `${Math.round((bytes / 1024) * 10) / 10} KB`;
}

// Pure cap check, exported for unit tests: returns a structured error object
// when the serialised result exceeds the cap, or null when it fits. The error
// carries the raw sizes (`bytes`, `limitBytes`) so callers can compose their
// own guidance on top of the generic message.
export function resultSizeError(resultJsonLength, maxResultBytes) {
  if (!(resultJsonLength > maxResultBytes)) return null;
  return {
    kind: 'result_too_large',
    bytes: resultJsonLength,
    limitBytes: maxResultBytes,
    message: `The script produced ${formatBytes(resultJsonLength)} of JSON, `
      + `over the ${formatBytes(maxResultBytes)} result limit. Return less data.`,
  };
}

// Ceiling on everything the VM has to JSON.parse in the parse phase — the tree
// plus the optional data / context / reference payloads. The phase watchdog
// bounds how LONG that parse may take; this bounds how much is handed to it in
// the first place, so an oversized payload fails fast with an actionable error
// instead of burning the whole serialization budget and dying as a timeout.
const MAX_INPUT_BYTES = 256 * 1024 * 1024;

// Total serialised input handed to the VM. Exported for unit tests.
export function inputSizeBytes({ tasksJson, dataJson, contextJson, refsJson } = {}) {
  return [tasksJson, dataJson, contextJson, refsJson]
    .reduce((total, json) => total + (typeof json === 'string' ? json.length : 0), 0);
}

// Pure cap check for the input side, mirroring resultSizeError.
export function inputSizeError(inputBytes, maxInputBytes) {
  if (!(inputBytes > maxInputBytes)) return null;
  return {
    kind: 'input_too_large',
    bytes: inputBytes,
    limitBytes: maxInputBytes,
    message: `The sandbox inputs total ${formatBytes(inputBytes)} of JSON (task tree plus data/context/reference `
      + `payloads), over the ${formatBytes(maxInputBytes)} input limit. Pass fewer rows or fewer columns.`,
  };
}

// ── Phase-aware watchdog ────────────────────────────────────────────────────
// The sandbox runs in three phases (parse → exec → serialize) and the worker
// posts a { type: 'phase', phase } progress message as each one starts. Only
// the EXEC phase can be bounded in-VM (QuickJS's native JSON.parse/stringify
// never poll the interrupt handler — measured), so the main-thread watchdog is
// the only thing that bounds parse/serialize: it terminates the worker when the
// CURRENT phase exceeds its own budget. Total wall time is then naturally
// bounded by the sum of the phase budgets — there is no single fixed kill time.
//
// serializationBudgetMs applies to parse and serialize EACH rather than as a
// shared pool: one timer reset per phase transition, no residual-budget
// bookkeeping, each phase individually bounded, and the worst-case total
// (deadlineMs + 2×budget + 3×grace) stays finite. The default is deliberately
// generous — a measured ~87 MB payload parses in ~2 s and stringifies in ~3 s
// in QuickJS-wasm, so 120 s is ~50x headroom for far larger schedules on slow
// hardware while still guaranteeing a wedged worker dies. Phase cost scales
// with schedule size, not code quality; only exec reflects the agent's code.

export const DEFAULT_DEADLINE_MS = 20000;
export const DEFAULT_SERIALIZATION_BUDGET_MS = 120000;

// Grace on top of every phase budget: covers worker startup / wasm compile
// attribution into 'parse', message latency, and the in-VM exec interrupt
// firing first so the sandbox's own (better) error wins when it can.
const WATCHDOG_SLACK_MS = 5000;

// Watchdog budget for a phase, grace included. Exported for unit tests.
export function phaseBudgetMs(phase, {
  deadlineMs = DEFAULT_DEADLINE_MS,
  serializationBudgetMs = DEFAULT_SERIALIZATION_BUDGET_MS,
  graceMs = WATCHDOG_SLACK_MS,
} = {}) {
  return (phase === 'exec' ? deadlineMs : serializationBudgetMs) + graceMs;
}

// Structured error for a phase-budget overrun. Exec keeps the unbounded-loop
// hint (the deadline there really does reflect the code); parse/serialize must
// NOT — their cost is payload size, and retrying the same code cannot help.
// Exported for unit tests.
export function phaseTimeoutError(phase, {
  deadlineMs = DEFAULT_DEADLINE_MS,
  serializationBudgetMs = DEFAULT_SERIALIZATION_BUDGET_MS,
} = {}) {
  if (phase === 'exec') {
    return {
      kind: 'deadline',
      phase,
      message: `Your code did not return within its ${Math.round(deadlineMs / 1000)}s execution budget `
        + 'and the sandbox was force-terminated. Check for an unbounded loop, or split the work.',
    };
  }
  const action = phase === 'serialize'
    ? 'serialize the result'
    : 'deserialize the schedule payload';
  return {
    kind: 'serialization_budget',
    phase,
    message: `The sandbox took more than ${Math.round(serializationBudgetMs / 1000)}s to ${action} `
      + 'and was force-terminated. The schedule payload is too large to (de)serialize within the budget — '
      + 'this is a data-size limit, not a bug in your code; retrying the same code will not help.',
  };
}

/**
 * Run an agent transform script against a task tree off the main thread.
 *
 * @param {Object}   params
 * @param {string}   params.code            The transform source.
 * @param {Array}    params.tasks           The nested task tree (may be reactive).
 * @param {string}  [params.dataJson]       Agent payload, ALREADY serialised → `data`.
 * @param {string}  [params.contextJson]    Second agent payload → `context`.
 * @param {string}  [params.refsJson]       Host reference data, ALREADY serialised, as
 *                                          `{ locations, teams, scheduleTemplate }` →
 *                                          one global each. Callers serialise these
 *                                          themselves: only strings cross to the
 *                                          worker, so nothing here can trip
 *                                          structured clone on a Vue proxy.
 * @param {Object}  [params.options]        Passed through to runTransform
 *                                          (memoryLimitBytes, deadlineMs, maxLogs,
 *                                          requireArrayResult), plus the
 *                                          runner-side maxResultBytes cap,
 *                                          maxInputBytes cap and
 *                                          serializationBudgetMs (per-phase
 *                                          watchdog budget for parse/serialize).
 * @returns {Promise<{ ok, result?, resultJson?, logs, inputBytes,
 *                     parseMs?, execMs?, serializeMs?, elapsedMs, error? }>}
 *   On ok, `result` is the parsed value and `resultJson` the raw string.
 *   `inputBytes` is the total JSON handed to the VM (tree + payloads).
 */
export async function runTransformOnWorker({
  code,
  tasks,
  dataJson = undefined,
  contextJson = undefined,
  refsJson = undefined,
  options = {},
}) {
  const budgets = {
    deadlineMs: Number.isFinite(options.deadlineMs) ? options.deadlineMs : DEFAULT_DEADLINE_MS,
    serializationBudgetMs: Number.isFinite(options.serializationBudgetMs)
      ? options.serializationBudgetMs : DEFAULT_SERIALIZATION_BUDGET_MS,
  };
  const maxResultBytes = Number.isFinite(options.maxResultBytes) ? options.maxResultBytes : MAX_RESULT_BYTES;
  const maxInputBytes = Number.isFinite(options.maxInputBytes) ? options.maxInputBytes : MAX_INPUT_BYTES;
  const startedAt = Date.now();

  let tasksJson;
  try {
    tasksJson = JSON.stringify(deepToRaw(tasks) ?? []);
  } catch (error) {
    return {
      ok: false,
      logs: [],
      elapsedMs: Date.now() - startedAt,
      error: { kind: 'exception', message: `Failed to serialise the task tree: ${error?.message || error}` },
    };
  }

  // Size accounting covers every payload the VM has to JSON.parse, not just the
  // tree: the parse phase (and therefore the serialization budget that bounds
  // it) now pays for data/context/refs too, so they must count toward the same
  // input ceiling. Refused before the worker is even spawned.
  const inputBytes = inputSizeBytes({ tasksJson, dataJson, contextJson, refsJson });
  const inputError = inputSizeError(inputBytes, maxInputBytes);
  if (inputError) {
    return { ok: false, logs: [], inputBytes, elapsedMs: Date.now() - startedAt, error: inputError };
  }

  const raw = await runInWorker({ code, tasksJson, dataJson, contextJson, refsJson, options, budgets });
  if (!raw.ok) return { ...raw, inputBytes };

  // Guard the result size before parsing it back into memory on this thread.
  if (typeof raw.resultJson === 'string') {
    const sizeError = resultSizeError(raw.resultJson.length, maxResultBytes);
    if (sizeError) {
      return {
        ok: false,
        logs: raw.logs || [],
        inputBytes,
        elapsedMs: raw.elapsedMs ?? (Date.now() - startedAt),
        error: sizeError,
      };
    }
  }

  let result;
  try {
    result = JSON.parse(raw.resultJson);
  } catch (error) {
    return {
      ok: false,
      logs: raw.logs || [],
      inputBytes,
      elapsedMs: raw.elapsedMs ?? (Date.now() - startedAt),
      error: { kind: 'exception', message: `The transform result was not valid JSON: ${error?.message || error}` },
    };
  }

  return { ...raw, result, inputBytes };
}

function runInWorker({ code, tasksJson, dataJson, contextJson, refsJson, options, budgets }) {
  // No Worker (SSR / unit tests / locked-down embeds): run inline. The in-VM
  // interrupt handler still bounds user code; parse/serialize are unbounded
  // here (no watchdog can terminate the thread we're on), matching the
  // pre-worker behaviour of these environments.
  if (typeof Worker === 'undefined') {
    return import('./quickJsSandbox').then(({ runTransform }) =>
      runTransform({ code, tasksJson, dataJson, contextJson, refsJson, ...(options || {}) }));
  }

  return new Promise((resolve) => {
    let worker;
    try {
      worker = new Worker(new URL('../workers/transform.worker.js', import.meta.url), { type: 'module' });
    } catch {
      // Worker construction failed (CSP, bundling) — fall back inline.
      import('./quickJsSandbox').then(({ runTransform }) =>
        runTransform({ code, tasksJson, dataJson, contextJson, refsJson, ...(options || {}) })).then(resolve);
      return;
    }

    const startedAt = Date.now();
    let settled = false;
    let watchdog = null;
    // Main-thread view of phase transitions: lets a watchdog kill report how
    // far the run got (approximate per-phase timings) even though the worker
    // died without posting a result.
    let currentPhase = 'parse'; // worker startup + wasm load attribute to parse
    let phaseStartedAt = startedAt;
    const observedTimings = { parseMs: null, execMs: null, serializeMs: null };

    const finish = (value) => {
      if (settled) return;
      settled = true;
      clearTimeout(watchdog);
      try { worker.terminate(); } catch { /* already gone */ }
      resolve(value);
    };

    // Hard terminate remains the backstop — but scoped to the CURRENT phase's
    // own budget, so a slow parse of a huge payload no longer eats the exec
    // budget (or vice versa). Rearmed on every phase-transition message.
    const armWatchdog = () => {
      clearTimeout(watchdog);
      watchdog = setTimeout(() => {
        observedTimings[`${currentPhase}Ms`] = Date.now() - phaseStartedAt;
        finish({
          ok: false,
          logs: [],
          ...observedTimings,
          elapsedMs: Date.now() - startedAt,
          error: phaseTimeoutError(currentPhase, budgets),
        });
      }, phaseBudgetMs(currentPhase, budgets));
    };
    armWatchdog();

    worker.onmessage = (event) => {
      // Phase progress: postMessage delivers even while the worker thread then
      // blocks inside the VM, so transitions arrive in real time.
      if (event.data && event.data.type === 'phase') {
        const now = Date.now();
        if (event.data.phase !== currentPhase) {
          observedTimings[`${currentPhase}Ms`] = now - phaseStartedAt;
          currentPhase = event.data.phase;
          phaseStartedAt = now;
        }
        armWatchdog();
        return;
      }
      finish(event.data);
    };
    worker.onerror = (event) => finish({
      ok: false,
      logs: [],
      elapsedMs: 0,
      error: { kind: 'exception', message: event?.message || 'Transform worker crashed.' },
    });

    try {
      worker.postMessage({ code, tasksJson, dataJson, contextJson, refsJson, options });
    } catch (error) {
      finish({
        ok: false,
        logs: [],
        elapsedMs: 0,
        error: { kind: 'exception', message: `Failed to start the transform: ${error?.message || error}` },
      });
    }
  });
}
